import { CustomerService, NumericFormatterValueConverter, PortfolioReportCache, property, widget } from '@dynamik/common';
import { combineLatestWith, debounceTime, delayWhen, ReplaySubject, switchMap, takeUntil, tap } from '../../../rxjs-local';
import { InstrumentType, InstrumentValueGroup, InstrumentValuesReportViewModel } from '@dynamik/common/clients/reporting-clients';
import { ColumnState, GetContextMenuItemsParams, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { getExcelStyles } from '../../../resources/agGrid/excel-styles';
import { customElement } from 'aurelia-templating';
import { ThemeService } from '../../../services/theme-service';

@customElement('portfolio-overview-by-type-widget')
@widget('Portfolio overview by type', false, true, 'Description')
export class PortfolioOverviewByTypeWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache,
    bindingEngine: BindingEngine, private numericFormatter: NumericFormatterValueConverter, public themeService: ThemeService) {
    super(element, bindingEngine, customerService);
  }

  gridApi: GridApi<InstrumentValueGroup>;
  instrumentValues: InstrumentValuesReportViewModel[];
  suppressSaveColumnState: boolean;
  gridOptions: GridOptions<InstrumentValueGroup>;
  gridReady$ = new ReplaySubject<void>(1);
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number / 1000000, this.precision);

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Data')
  hidePaging: boolean = false;

  @property('Data')
  precision?: number = 2;

  @property('Table')
  fontSize: number = 8;

  @property('Table')
  rowHeight: number = 20;

  override subscribeToFilter() {
    this.customerService.customerAndDates$
      .pipe(
        delayWhen(() => this.gridReady$),
        combineLatestWith(this.refresh$),
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.gridApi.setGridOption('loading', true);
          this.notifyLoading();
        }),
        switchMap(async ([filter]) => this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, undefined, filter.dates.fromStr, filter.dates.toStr, false)
          .catch(e => e as Error)),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.gridApi.setGridOption('loading', false);
        if (x instanceof Error) {
          this.error = x;
        } else {
          const data = x.filter(x => x.date.substring(0, 10) == this.customerService.selectedDates.toStr);
          const total = this.getTotal(data);
          data.forEach(x => {
            x.costPricePercent = x.costPrice / total.costPrice * 100;
            x.profitLossYearToDatePercent = x.costPriceYearToDateAdjusted !== 0 ? x.profitLossYearToDate / x.costPriceYearToDateAdjusted * 100 : 0;
            x.estimatedValueChange = x.estimatedValue - x.estimatedValueFrom;
           });
           this.gridApi.setGridOption('rowData', data);
           this.gridApi.setGridOption('pinnedBottomRowData', [total]);
           this.excelData = {
             fileName: 'Portfolio Overview by Type',
             data: { Items: data }
           };
           this.notifyLoaded();
        }
      });
  }

  getTotal(x: InstrumentValueGroup[]) {
    const total = x.reduce((p, c) => {
      p.estimatedValueFrom += c.estimatedValueFrom;
      p.estimatedValue += c.estimatedValue;
      p.costPrice += c.costPrice;
      p.profitLossYearToDate += c.profitLossYearToDate;
      p.costPriceYearToDateAdjusted += c.costPriceYearToDateAdjusted;
      return p;
    }, new InstrumentValueGroup({
      group: 'TOTAL', estimatedValueFrom: 0, estimatedValue: 0, costPrice: 0, profitLossYearToDate: 0, costPriceYearToDateAdjusted: 0, costPricePercent: 100
    }));
    total.estimatedValueChange = total.estimatedValue - total.estimatedValueFrom;
    total.profitLossYearToDatePercent = total.costPriceYearToDateAdjusted !== 0 ? total.profitLossYearToDate / total.costPriceYearToDateAdjusted * 100 : 0;
    return total;
  }

  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
      popupParent: document.body,
      rowHeight: this.rowHeight,
      excelStyles: getExcelStyles(9),
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      paginationAutoPageSize: true,
      pagination: true,
      paginateChildRows: true,
      suppressAggFuncInHeader: true,
      groupHideOpenParents: false,
      animateRows: false,
      debug: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: params => {
        if (params.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      getRowId: x => x.data.group,
      onGridReady: params => {
        this.gridApi = params.api;
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.columnStates?.length) {
          this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
        this.gridReady$.next();
      }
    };
  }

  getPageCount(): number {
    return this.gridApi.paginationGetTotalPages();
  }

  async setPage(page: number): Promise<void> {
    if (page === this.gridApi.paginationGetCurrentPage()) {
      return;
    }
    this.gridApi.paginationGoToPage(page);
    // give the grid time to render
    await Promise.delay(100);
  }

  override configChanged(key: keyof PortfolioOverviewByTypeWidget) {
    switch (key) {
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }

  saveColumnState() {
    if (this.suppressSaveColumnState) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  rowHeightChanged() {
    this.gridOptions.rowHeight = this.rowHeight;
    this.gridApi.resetRowHeights();
  }

  getContextMenuItems(params: GetContextMenuItemsParams<InstrumentValueGroup>) {
    return [
      'autoSizeAll',
      'resetColumns',
      { name: 'Fit All Columns', action: () => this.gridApi.sizeColumnsToFit() },
      'separator',
      {
        name: 'Export to Csv',
        action: async () => params.api.exportDataAsCsv({ columnSeparator: ';' }),
      },
      {
        name: 'Export to Excel',
        action: async () => params.api.exportDataAsExcel({

          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: false,
          onlySelected: false,
          fileName: 'PortfolioOverviewByType',
          sheetName: 'PortfolioOverviewByType'
        }),
      }
    ];
  }

}
